/**
 * PATH: apps/quote/src/pages/QuoteDetailPage.tsx
 * PURPOSE: Single quote detail view
 */

import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, ArrowLeft, CheckCircle, Copy, Download, Edit, Link2, RefreshCw, Send, Unlink, XCircle } from 'lucide-react'
import { cn, formatCurrency, formatDate, getStatusColor, getStatusLabel } from '@/lib/utils'
import { quoteApi, type QuoteDetail } from '@/lib/quoteApi'
import { useQuoteTabStore } from '../config/tabs'

function toNumber(value: string | number | null | undefined): number {
  if (typeof value === 'number') return value
  if (typeof value === 'string') {
    const n = Number.parseFloat(value)
    return Number.isFinite(n) ? n : 0
  }
  return 0
}

export default function QuoteDetailPage() {
  const { id } = useParams<{ id: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const updateActiveTab = useQuoteTabStore(s => s.updateActiveTab)
  const [trackerQuoteIdInput, setTrackerQuoteIdInput] = useState('')

  const quoteQuery = useQuery({
    queryKey: ['quote', 'detail', id],
    queryFn: async () => quoteApi.getQuote(id!),
    enabled: !!id,
  })

  const exportPdfMutation = useMutation({
    mutationFn: async () => quoteApi.exportQuote(id!, 'pdf'),
    onSuccess: (blob) => {
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `quote-${quoteQuery.data?.quote_number || id}.pdf`
      a.click()
      window.URL.revokeObjectURL(url)
    },
  })

  const duplicateMutation = useMutation({
    mutationFn: async () => quoteApi.duplicateQuote(id!, false),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] })
      if (created?.id) navigate(`/builder/${created.id}`)
    },
  })

  const markSentMutation = useMutation({
    mutationFn: async () => quoteApi.markSent(id!),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['quote', 'detail', id] }),
  })

  const markAcceptedMutation = useMutation({
    mutationFn: async () => quoteApi.markAccepted(id!),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['quote', 'detail', id] }),
  })

  const markRejectedMutation = useMutation({
    mutationFn: async () => quoteApi.markRejected(id!),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['quote', 'detail', id] }),
  })

  type LinkStatusResponse =
    | { is_linked: true; tracker_quote_ids: string[] }
    | { is_linked: false }

  const linkStatusQuery = useQuery<LinkStatusResponse>({
    queryKey: ['quote', 'trackerLinkStatus', id],
    queryFn: async () => (await quoteApi.getLinkStatus({ builder_quote_id: id! })) as LinkStatusResponse,
    enabled: !!id,
  })

  useEffect(() => {
    if (quoteQuery.data?.quote_number) {
      updateActiveTab(location.pathname, quoteQuery.data.quote_number)
    }
  }, [quoteQuery.data?.quote_number, location.pathname, updateActiveTab])

  const linkToTrackerMutation = useMutation({
    mutationFn: async () =>
      quoteApi.linkToTracker({
        builder_quote_id: id!,
        tracker_quote_id: trackerQuoteIdInput.trim(),
      }),
    onSuccess: () => linkStatusQuery.refetch(),
  })

  const unlinkFromTrackerMutation = useMutation({
    mutationFn: async () => quoteApi.unlinkFromTracker({ builder_quote_id: id! }),
    onSuccess: () => linkStatusQuery.refetch(),
  })

  // NOTE: `trackerLink` is a plain alias (NOT a hook) declared BEFORE any
  // conditional early returns below. If we declare it as `useMemo()` AFTER
  // the loading/error returns, React detects a different hook count between
  // the loading render (returns early after 9 hooks) and the success render
  // (executes the 10th hook, useMemo), and throws Minified error #310
  // ("Rendered more hooks than during the previous render"), which the
  // ErrorBoundary catches as a white "Something went wrong" screen. Plain
  // assignment keeps hook order stable across every render path.
  const trackerLink = linkStatusQuery.data

  if (quoteQuery.isLoading) {
    return <div className="p-6 text-sm text-gray-500 dark:text-gray-400">Loading quote…</div>
  }

  if (quoteQuery.isError) {
    // Surface 404 specifically so users hitting a stale URL (e.g. a quote
    // wiped by a demo reload) get an actionable message + a way out, rather
    // than just "Failed to load".
    const err = quoteQuery.error as { response?: { status?: number } } | undefined
    const notFound = err?.response?.status === 404
    return (
      <div className="p-6">
        <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 flex-shrink-0" />
            <span className="flex-1">
              {notFound
                ? `Quote not found (id ${id}). It may have been deleted or moved.`
                : 'Failed to load quote.'}
            </span>
            <button
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-red-200 dark:border-red-800 hover:bg-red-100 dark:hover:bg-red-900/30"
              onClick={() => quoteQuery.refetch()}
            >
              <RefreshCw className="h-4 w-4" />
              Retry
            </button>
          </div>
          <div className="mt-3">
            <Link to="/quotes" className="inline-flex items-center gap-2 text-red-700 dark:text-red-300 underline">
              <ArrowLeft className="h-4 w-4" />
              Back to quotes list
            </Link>
          </div>
        </div>
      </div>
    )
  }

  const quote = quoteQuery.data as QuoteDetail
  const subtotal = toNumber(quote.total_excl_gst)
  const gst = toNumber(quote.gst_amount)
  const total = toNumber(quote.total_incl_gst)

  return (
    <div className="p-6 space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
        <Link to="/quotes" className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-300">
          <ArrowLeft className="h-4 w-4" />
          Quotes
        </Link>
        <span>/</span>
        <span className="text-gray-900 dark:text-white">{quote.quote_number}</span>
      </div>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{quote.quote_number}</h1>
            <span className={cn('px-3 py-1 rounded-full text-sm font-medium', getStatusColor(String(quote.status)))}>
              {quote.status_display || getStatusLabel(String(quote.status))}
            </span>
          </div>
          <p className="text-gray-600 dark:text-gray-400 mt-1">{quote.project_name || '—'}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => exportPdfMutation.mutate()}
            disabled={exportPdfMutation.isPending}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            <Download className="h-4 w-4" />
            PDF
          </button>
          <button
            onClick={() => duplicateMutation.mutate()}
            disabled={duplicateMutation.isPending}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            <Copy className="h-4 w-4" />
            Duplicate
          </button>
          <Link
            to={`/builder/${id}`}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <Edit className="h-4 w-4" />
            Edit
          </Link>
          <button
            onClick={() => markSentMutation.mutate()}
            disabled={markSentMutation.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <Send className="h-4 w-4" />
            Send
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Quote Details */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
            <h2 className="font-semibold text-gray-900 dark:text-white mb-4">Quote Details</h2>
            <div className="prose dark:prose-invert max-w-none">
              <p className="text-gray-700 dark:text-gray-300">{quote.project_description || '—'}</p>
            </div>
          </div>

          {/* Totals */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
            <div className="p-6 border-b border-gray-100 dark:border-gray-700">
              <h2 className="font-semibold text-gray-900 dark:text-white">Totals</h2>
            </div>
            <div className="p-6 space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Subtotal</span>
                <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(subtotal)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">GST (10%)</span>
                <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(gst)}</span>
              </div>
              <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                <div className="flex justify-between">
                  <span className="font-bold text-gray-900 dark:text-white">Total</span>
                  <span className="font-bold text-2xl text-blue-600">{formatCurrency(total)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Client Info */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Client</h3>
            <div className="space-y-3">
              <p className="font-medium text-gray-900 dark:text-white">{quote.client_name || '—'}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">{quote.client_email || '—'}</p>
            </div>
          </div>

          {/* CRM Link */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900 dark:text-white">CRM Link</h3>
              <button
                onClick={() => linkStatusQuery.refetch()}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                title="Refresh link status"
              >
                <RefreshCw className={cn('h-4 w-4 text-gray-600 dark:text-gray-400', linkStatusQuery.isFetching && 'animate-spin')} />
              </button>
            </div>

            {linkStatusQuery.isLoading ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">Checking link status…</p>
            ) : linkStatusQuery.isError ? (
              <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                <span className="flex-1">{(linkStatusQuery.error as Record<string, unknown>)?.message || 'Failed to load link status'}</span>
              </div>
            ) : trackerLink?.is_linked ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-300">
                  <Link2 className="h-4 w-4" />
                  Linked to CRM
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Tracker quote IDs:
                  <ul className="mt-2 space-y-1">
                    {trackerLink.tracker_quote_ids.map((tid) => (
                      <li key={tid} className="font-mono text-[11px] text-gray-600 dark:text-gray-300 break-all">
                        {tid}
                      </li>
                    ))}
                  </ul>
                </div>
                <button
                  onClick={() => {
                    if (window.confirm('Unlink this builder quote from CRM?')) unlinkFromTrackerMutation.mutate()
                  }}
                  disabled={unlinkFromTrackerMutation.isPending}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-red-200 text-red-700 dark:border-red-800 dark:text-red-300 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50"
                >
                  <Unlink className="h-4 w-4" />
                  Unlink
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                  <Link2 className="h-4 w-4" />
                  Not linked
                </div>
                <div>
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Tracker Quote ID (UUID)</label>
                  <input
                    value={trackerQuoteIdInput}
                    onChange={(e) => setTrackerQuoteIdInput(e.target.value)}
                    placeholder="Paste CRM quote UUID…"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm font-mono"
                  />
                </div>
                <button
                  onClick={() => linkToTrackerMutation.mutate()}
                  disabled={linkToTrackerMutation.isPending || !trackerQuoteIdInput.trim()}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  <Link2 className="h-4 w-4" />
                  Link to CRM
                </button>
                {linkToTrackerMutation.isError ? (
                  <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">
                    {(linkToTrackerMutation.error as Record<string, unknown>)?.message || 'Failed to link'}
                  </div>
                ) : null}
              </div>
            )}
          </div>

          {/* Dates */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Timeline</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Created</span>
                <span className="text-gray-900 dark:text-white">
                  {quote.created_at ? formatDate(quote.created_at) : '—'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Valid Until</span>
                <span className="text-gray-900 dark:text-white">
                  {quote.valid_until ? formatDate(quote.valid_until) : '—'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Created By</span>
                <span className="text-gray-900 dark:text-white">{(quote as Record<string, unknown>).created_by_name || '—'}</span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Quick Actions</h3>
            <div className="space-y-2">
              <button
                onClick={() => markAcceptedMutation.mutate()}
                disabled={markAcceptedMutation.isPending}
                className="w-full flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded-lg hover:bg-green-200 dark:hover:bg-green-900/50 disabled:opacity-50"
              >
                <CheckCircle className="h-4 w-4" />
                Mark as Accepted
              </button>
              <button
                onClick={() => markRejectedMutation.mutate()}
                disabled={markRejectedMutation.isPending}
                className="w-full flex items-center gap-2 px-4 py-2 bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/50 disabled:opacity-50"
              >
                <XCircle className="h-4 w-4" />
                Mark as Rejected
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

